import { useFlag } from '@calendly/feature-check-react'
import { useCallback, useMemo } from 'react'

import { buildFilters } from 'components/meetings/filter_modal/utils'
import { useFilters } from 'hooks/meetings/filters'
import { useScope } from 'hooks/meetings/use_scope'
import {
  useLazyGetMeetingsFilterCountQuery,
  useLazyGetMeetingsSupergraphFilterCountQuery,
} from 'shared/api/meetings'
import { useCurrentTime } from 'shared/contexts'

import { useLookupUuidsQuery } from '../api'
import { buildSupergraphCountInput } from './search_data/supergraph_meeting_search_utils'
import { useSelectedView } from './use_selected_view'
import { allowedScopeTypes } from '../../../constants/meetings'
import { mapScopeTypeToBackendValue } from '../utils'

const buildMeetingsFilterCountVariables = ({
  filterParams,
  scopeType,
  scopeId,
  selectedView
}) => {
  const filters = buildFilters(
    scopeType,
    scopeId,
    selectedView,
    filterParams,
    true,
    allowedScopeTypes,
    mapScopeTypeToBackendValue
  )

  return {
    forCount: true,
    ...filters,
  }
}

const useFilterParamsWithUuids = (filterParams) => {
  const { data: uuids } = useLookupUuidsQuery({ filters: filterParams })

  if (!uuids) {
    return null
  }

  return {
    ...filterParams,
    ...uuids
  }
}

export const useFetchMeetingsFilterCountForAnalytics = (filterDefaults = {}) => {
  const { getFilterParams } = useFilters()
  const filterParams = getFilterParams(filterDefaults)
  const filtersToUse = useFilterParamsWithUuids(filterParams)
  const { scopeType, scopeId } = useScope()
  const selectedView = useSelectedView()
  const isSupergraphEnabled = useFlag('sa_meetings_page_with_supergraph', false)
  const [triggerLegacyFilterCount] = useLazyGetMeetingsFilterCountQuery()
  const [triggerSupergraphFilterCount] = useLazyGetMeetingsSupergraphFilterCountQuery()

  return useCallback(async (searchKeyword) => {
    if (!filtersToUse) {
      return 0
    }
    const filterParamsForCount = searchKeyword !== undefined
      ? { ...filtersToUse, search_keyword: searchKeyword }
      : filtersToUse
    try {
      const variables = buildMeetingsFilterCountVariables({
        filterParams: filterParamsForCount,
        scopeType,
        scopeId,
        selectedView
      })
      if (isSupergraphEnabled) {
        const input = buildSupergraphCountInput(variables)
        const total = await triggerSupergraphFilterCount(input).unwrap()
        return typeof total === 'number' ? total : 0
      }
      const data = await triggerLegacyFilterCount(variables).unwrap()
      return data?.meetings?.total ?? 0
    } catch {
      return 0
    }
  }, [
    filtersToUse,
    scopeType,
    scopeId,
    selectedView,
    isSupergraphEnabled,
    triggerLegacyFilterCount,
    triggerSupergraphFilterCount,
  ])
}

export const createUseResultsCount = (
  useGetMeetingsFilterCountQuery,
  useGetMeetingsSupergraphFilterCountQuery,
) => (filterParams, skip) => {
  const filtersToUse = useFilterParamsWithUuids(filterParams)
  const { scopeType, scopeId } = useScope()
  const selectedView = useSelectedView()
  const isSupergraphEnabled = useFlag('sa_meetings_page_with_supergraph', false)
  const currentTime = useCurrentTime()
  useMemo(
    () => currentTime.format('YYYY-MM-DD'),
    [currentTime],
  )
  const countVariables = useMemo(
    () => (skip
      ? null
      : buildMeetingsFilterCountVariables({
        filterParams: filtersToUse,
        scopeType,
        scopeId,
        selectedView
      })),
    [skip, filtersToUse, scopeId, scopeType, selectedView]
  )

  // Same object as legacy count query args; `forCount` is unused by buildSupergraphCountInput's mapper.
  const supergraphCountInput = useMemo(
    () => (skip || !isSupergraphEnabled || !countVariables
      ? null
      : buildSupergraphCountInput(countVariables)),
    [skip, isSupergraphEnabled, countVariables],
  )

  const { data: legacyData, isFetching: legacyFetching } = useGetMeetingsFilterCountQuery(
    countVariables ?? {},
    { skip: skip || isSupergraphEnabled },
  )

  const { data: superData, isFetching: superFetching } = useGetMeetingsSupergraphFilterCountQuery(
    supergraphCountInput ?? {},
    { skip: skip || !isSupergraphEnabled || !supergraphCountInput },
  )

  if (skip) {
    return {
      count: 0,
      isLoading: false
    }
  }

  let count
  if (isSupergraphEnabled) {
    count = typeof superData === 'number' ? superData : 0
  } else {
    count = legacyData?.meetings?.total || 0
  }

  return {
    count,
    isLoading: isSupergraphEnabled ? superFetching : legacyFetching
  }
}
